import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { css } from '@emotion/css'
import moment from 'moment'
import Swal from 'sweetalert2'
import IPInput from '../../../../../components/IPInput/IPInput'
import IPButton from '../../../../../components/IPButton/IPButton'
import Table from '../../../../../components/Table'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import { formatCurrencyTemlik } from '../../../../../base/utils/dataHelper'

import { IPKActivationException, IPKActivationExceptionHistoryItem } from '../models/model'
import {
  AddPKActivationExceptionApi,
  DeletePKActivationExceptionApi,
  GetPKActivationExceptionsApi,
  SetPKActivationExceptionStatusApi,
} from '../services/PKActivationExceptionApi'
import { GetSuccessFullApi } from '../../../CreditCardOperation/Successful/services/getSuccessFullApi'
import { ISuccessFullRequest, ISuccessFullResponse } from '../../../CreditCardOperation/Successful/models/models'

// Küçük, sağa yaslı yönlendirme butonu.
// Bootstrap "btn/btn-outline-*" sınıfları kullanılmıyor: tema CSS'i (assetWeb/css/style.css)
// bu sınıfları eziyor ve hover'da yazıyı beyaza çevirip zemini kaybettiriyor. Tema mavisi (#2250fc)
// burada !important ile sabitlendi ki tema araya girmesin.
const navButtonStyle = css`
  padding: 5px 12px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  line-height: 1.4 !important;
  white-space: nowrap;
  border: 1px solid #2250fc !important;
  border-radius: 4px !important;
  background-color: #2250fc !important;
  color: #ffffff !important;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;

  &:hover {
    background-color: #1a41d6 !important;
    border-color: #1a41d6 !important;
    color: #ffffff !important;
  }
`

// Tablo içi işlem butonları da aynı tema sorununa takılıyor; renk aynı yöntemle sabitleniyor.
const actionButtonStyle = (color: string) => css`
  padding: 2px 10px !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  line-height: 1.4 !important;
  white-space: nowrap;
  border: 1px solid ${color} !important;
  border-radius: 4px !important;
  background-color: #ffffff !important;
  color: ${color} !important;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  &:hover:not(:disabled) {
    background-color: ${color} !important;
    color: #ffffff !important;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

const paymentsButtonStyle = actionButtonStyle('#0d6efd')
const historyButtonStyle = actionButtonStyle('#6c757d')
const closeButtonStyle = actionButtonStyle('#fd7e14')
const openButtonStyle = actionButtonStyle('#198754')
const deleteButtonStyle = actionButtonStyle('#dc3545')

const formatDateTime = (value: any): string => {
  if (!value) return '-'
  const date = moment(value)
  return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : String(value)
}

// Backend değişiklik geçmişini tek alanda JSON dizi olarak tutuyor; burada parse ediyoruz.
// Bozuk/boş içerikte sayfa patlamasın diye boş dizi dönülür.
const parseHistory = (history?: string): IPKActivationExceptionHistoryItem[] => {
  if (!history) return []
  try {
    const parsed = JSON.parse(history)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function PKActivationExceptions(props: BaseComponentWithContextProps) {
  const navigate = useNavigate()

  const [imei, setImei] = useState('')
  const [description, setDescription] = useState('')
  const [list, setList] = useState<IPKActivationException[]>([])
  const [selected, setSelected] = useState<IPKActivationException | null>(null)
  const [paymentData, setPaymentData] = useState<ISuccessFullResponse[] | null>(null)

  const showError = (title: string, err: any) => {
    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      err?.description ||
      err ||
      'Beklenmeyen bir hata oluştu.'
    Swal.fire({ icon: 'error', title, text: String(msg) })
  }

  const getList = useCallback(async () => {
    try {
      props.showLoading()
      const api = new GetPKActivationExceptionsApi({})
      const data = await api.getPKActivationExceptions()
      setList(data ?? [])
    } catch (err) {
      showError('Muafiyet Listesi Getirilemedi', err)
      setList([])
    } finally {
      props.hideLoading()
    }
  }, [])

  useEffect(() => {
    getList()
  }, [])

  const onAdd = async () => {
    if (!imei.trim()) {
      Swal.fire({ icon: 'warning', title: 'Eksik bilgi', text: 'IMEI numarası gereklidir.' })
      return
    }

    try {
      props.showLoading()
      const api = new AddPKActivationExceptionApi({})
      await api.addPKActivationException({ Imei: imei.trim(), Description: description.trim() })
      setImei('')
      setDescription('')
      Swal.fire({ icon: 'success', title: 'IMEI muafiyet listesine eklendi.' })
      await getList()
    } catch (err) {
      showError('IMEI Eklenemedi', err)
    } finally {
      props.hideLoading()
    }
  }

  const onToggleStatus = async (row: IPKActivationException) => {
    const closing = row.IsActive

    const confirm = await Swal.fire({
      icon: 'question',
      title: closing ? 'Muafiyeti kapat' : 'Muafiyeti yeniden aç',
      html: closing
        ? `<b>${row.Imei}</b> için muafiyet kapatılacak. Aktivasyon kontrolü bu IMEI'de yeniden uygulanacak.<br/><br/>Kayıt ve ödeme bilgisi silinmez.`
        : `<b>${row.Imei}</b> için muafiyet yeniden açılacak.`,
      showCancelButton: true,
      confirmButtonText: closing ? 'Kapat' : 'Aç',
      cancelButtonText: 'Vazgeç',
    })

    if (!confirm.isConfirmed) return

    try {
      props.showLoading()
      const api = new SetPKActivationExceptionStatusApi({})
      await api.setPKActivationExceptionStatus({ Id: row.Id, IsActive: !row.IsActive })
      await getList()
    } catch (err) {
      showError('Durum Güncellenemedi', err)
    } finally {
      props.hideLoading()
    }
  }

  const onDelete = async (row: IPKActivationException) => {
    if (!row.CanDelete) {
      Swal.fire({
        icon: 'warning',
        title: 'Silinemez',
        text: 'Bu IMEI için ödeme yapıldığı için kayıt silinemez. Ödemeyi engellemek için kaydı kapatabilirsiniz.',
      })
      return
    }

    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Kaydı sil',
      html: `<b>${row.Imei}</b> muafiyet listesinden silinecek.`,
      showCancelButton: true,
      confirmButtonText: 'Sil',
      cancelButtonText: 'Vazgeç',
    })

    if (!confirm.isConfirmed) return

    try {
      props.showLoading()
      const api = new DeletePKActivationExceptionApi({})
      await api.deletePKActivationException({ Id: row.Id })
      if (selected?.Id === row.Id) {
        setSelected(null)
        setPaymentData(null)
      }
      await getList()
    } catch (err) {
      showError('Kayıt Silinemedi', err)
    } finally {
      props.hideLoading()
    }
  }

  const onShowPayments = async (row: IPKActivationException) => {
    setSelected(row)

    if (!row.OrderNumbers || row.OrderNumbers.length === 0) {
      setPaymentData([])
      return
    }

    try {
      props.showLoading()
      const paymentApi = new GetSuccessFullApi({})
      const payload: ISuccessFullRequest = { OrderNumberList: row.OrderNumbers }
      const data = await paymentApi.getSuccessFull(payload)
      setPaymentData(data ?? [])
    } catch (err) {
      showError('Ödeme Bilgilerini Getirirken Hata!', err)
      setPaymentData(null)
    } finally {
      props.hideLoading()
    }
  }

  const onShowHistory = (row: IPKActivationException) => {
    const history = parseHistory(row.History)

    if (history.length === 0) {
      Swal.fire({ icon: 'info', title: `${row.Imei}`, text: 'Geçmiş kaydı bulunmuyor.' })
      return
    }

    const rows = history
      .map(
        h => `<tr>
            <td style="padding:4px 8px;border:1px solid #ddd">${h.Action || '-'}</td>
            <td style="padding:4px 8px;border:1px solid #ddd">${h.UserName || '-'}</td>
            <td style="padding:4px 8px;border:1px solid #ddd">${formatDateTime(h.Date)}</td>
            <td style="padding:4px 8px;border:1px solid #ddd">${h.Note || '-'}</td>
          </tr>`
      )
      .join('')

    Swal.fire({
      title: `Geçmiş — ${row.Imei}`,
      width: 760,
      html: `<table style="border-collapse:collapse;font-size:13px;margin:0 auto">
          <thead><tr>
            <th style="padding:4px 8px;border:1px solid #ddd">İşlem</th>
            <th style="padding:4px 8px;border:1px solid #ddd">Kullanıcı</th>
            <th style="padding:4px 8px;border:1px solid #ddd">Tarih</th>
            <th style="padding:4px 8px;border:1px solid #ddd">Not</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>`,
    })
  }

  const columns = useMemo(
    () => [
      { Header: 'IMEI', accessor: 'imei' },
      { Header: 'Açıklama', accessor: 'aciklama' },
      { Header: 'Durum', accessor: 'durum' },
      { Header: 'Ekleyen', accessor: 'ekleyen' },
      { Header: 'Ekleme Tarihi', accessor: 'eklemeTarihi' },
      { Header: 'Son İşlem', accessor: 'sonIslem' },
      { Header: 'Ödenen (TL)', accessor: 'odenen' },
      { Header: 'İşlemler', accessor: 'islemler' },
    ],
    []
  )

  const tableData = useMemo(
    () =>
      list.map(item => {
        const history = parseHistory(item.History)
        const lastEntry = history.length > 0 ? history[history.length - 1] : undefined

        return {
          imei: item.Imei,
          aciklama: item.Description || '-',
          durum: item.IsActive ? (
            <span style={{ color: 'green', fontWeight: 600 }}>AKTİF</span>
          ) : (
            <span style={{ color: 'red', fontWeight: 600 }}>KAPALI</span>
          ),
          ekleyen: item.CreatedUserName || '-',
          eklemeTarihi: formatDateTime(item.CreatedDate),
          sonIslem: lastEntry
            ? `${lastEntry.Action} · ${lastEntry.UserName || '-'} · ${formatDateTime(lastEntry.Date)}`
            : '-',
          odenen: formatCurrencyTemlik(item.TotalPaid),
          islemler: (
            <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
              <button type='button' className={paymentsButtonStyle} onClick={() => onShowPayments(item)}>
                Ödemeler
              </button>
              <button type='button' className={historyButtonStyle} onClick={() => onShowHistory(item)}>
                Geçmiş
              </button>
              <button
                type='button'
                className={item.IsActive ? closeButtonStyle : openButtonStyle}
                onClick={() => onToggleStatus(item)}
              >
                {item.IsActive ? 'Kapat' : 'Aç'}
              </button>
              <button
                type='button'
                className={deleteButtonStyle}
                disabled={!item.CanDelete}
                title={item.CanDelete ? '' : 'Ödemesi olan IMEI silinemez, kapatabilirsiniz.'}
                onClick={() => onDelete(item)}
              >
                Sil
              </button>
            </div>
          ),
        }
      }),
    [list, selected]
  )

  const odemeDataColumns = useMemo(
    () => [
      { Header: 'Durum', accessor: (row: any) => (row.Cancelled ? 'İptal Edildi' : 'Geçerli') },
      { Header: 'Sipariş No', accessor: 'OrderNumber' },
      { Header: 'Makbuz No', accessor: 'SapTakipNO' },
      { Header: 'Bayi Kodu', accessor: (row: any) => row.DealaerCode?.replace('0000', '') },
      { Header: 'İşlem Zamanı', accessor: (row: any) => new Date(row.ProcessTime).toLocaleString('tr-TR') },
      { Header: 'Banka', accessor: 'Bank' },
      { Header: 'Kart Adı', accessor: 'CardName' },
      { Header: 'Bayi Adı', accessor: 'DealerName' },
      {
        Header: 'Taksit',
        accessor: (row: any) =>
          row.PayInstallment !== '0' && row.PayInstallment !== '1' ? `${row.PayInstallment} taksit` : 'Tek Çekim',
      },
      { Header: 'Tutar', accessor: 'RealPrice' },
      { Header: 'Para Birimi', accessor: 'Currency' },
    ],
    []
  )

  return (
    <div className=''>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header d-flex align-items-center justify-content-between'>
            <span className='h4 mb-0'>PK Aktivasyon Kontrolü Muafiyetleri</span>
            <button
              type='button'
              className={navButtonStyle}
              onClick={() => navigate('/admin/HomeAndSiteOperation/PKIMEIReport')}
            >
              IMEI Sorgulamaya Dön
            </button>
          </div>

          <div className='card-body'>
            {/* Bootstrap "alert" sınıfı kullanılmıyor: tema CSS'i (.alert) z-index: 9999 veriyor ve
                kutu loading ekranının (z-index: 1000) önüne geçiyor. */}
            <div
              className='py-2 px-3 mb-4'
              style={{
                fontSize: '0.85rem',
                backgroundColor: '#e7f1ff',
                border: '1px solid #b6d4fe',
                borderRadius: '0.25rem',
                color: '#084298',
              }}
            >
              Buradaki IMEI'ler PK ödemelerinde uygulanan <b>3 iş günü aktivasyon tarihi</b> kontrolünden muaf tutulur.
              Aşağıdaki kontroller muaf IMEI'lerde de <b>aynen uygulanmaya devam eder</b>:
              <ul className='mb-0 mt-1'>
                <li>
                  <b>"İlk başarılı ödeme 3 iş gününden önce yapıldı"</b> kontrolü.
                </li>
                <li>
                  <b>Temlik kaydı</b> uyarısı — temlik kaydı olan bir IMEI bu listeye eklense de ödeme yapılamaz,
                  muafiyet o hatayı aşmaz.
                </li>
              </ul>
            </div>

            <div className='row g-3 mb-4'>
              <div className='col-md-4'>
                <label className='form-label'>IMEI Numarası</label>
                <IPInput
                  value={imei}
                  onChange={(e: any) => setImei(e?.target?.value ?? e)}
                  type='text'
                  placeholder='IMEI numarası girin...'
                />
              </div>

              <div className='col-md-5'>
                <label className='form-label'>Açıklama (opsiyonel)</label>
                <IPInput
                  value={description}
                  onChange={(e: any) => setDescription(e?.target?.value ?? e)}
                  type='text'
                  placeholder='Muafiyet gerekçesi...'
                />
              </div>

              <div className='col-md-3 d-flex align-items-end'>
                <IPButton text='Listeye Ekle' disabled={!imei.trim()} onClick={onAdd} />
              </div>
            </div>

            <div className='row g-3 mb-4'>
              <div className='col-12'>
                <h6 className='mb-3'>Muafiyet Listesi</h6>
                {list.length > 0 ? (
                  <Table columns={columns} data={tableData} />
                ) : (
                  <div className='text-center py-4 text-muted'>
                    <p>Listede kayıt bulunmuyor.</p>
                  </div>
                )}
              </div>
            </div>

            {selected && (
              <div className='row g-3 mb-4'>
                <div className='col-12'>
                  <h6 className='mb-3'>
                    Ödeme Bilgileri — {selected.Imei}{' '}
                    {/* text-muted açık gri kalıp okunmuyordu; toplam tutar koyu ve belirgin. */}
                    <span style={{ color: '#212529', fontWeight: 600 }}>
                      (Toplam: {formatCurrencyTemlik(selected.TotalPaid)} TL)
                    </span>
                  </h6>
                  {paymentData && paymentData.length > 0 ? (
                    <Table columns={odemeDataColumns} data={paymentData} />
                  ) : (
                    <div className='text-muted'>Bu IMEI için ödeme kaydı bulunmuyor.</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(PKActivationExceptions)
